import { useForm, Controller } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { leadsApi } from '@/lib/api';
import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Field, NativeSelect, toast } from '@elbklar/ui';
import { TextField } from '@/components/form/Felder';
import { apiFehlerText } from '@/lib/fehlertext';
import { customerBusinessTypeOptions } from '@/types';

// Werte aus @/types — dieselben wie im Kundenformular und im core-Enum.
const businessTypeOptions = customerBusinessTypeOptions;

const industryOptions = [
  { value: 'privat_immobilien', label: 'Privat Immobilien' },
  { value: 'industrie', label: 'Industrie' },
  { value: 'oeffentlich', label: 'Öffentlich' },
  { value: 'gewerbe', label: 'Gewerbe' },
  { value: 'wohnungswirtschaft', label: 'Wohnungswirtschaft' },
];

interface ConvertFormData {
  customer_business_type: string;
  industry: string;
  street: string;
  postal_code: string;
  city: string;
  website: string;
  vat_number: string;
  managed_objects_count: string;
  contact_email: string;
  contact_role: string;
}

interface ConvertLeadModalProps {
  isOpen: boolean;
  onClose: () => void;
  leadId: string;
  leadName: string;
  /**
   * Die Nummer, die der Lead schon trägt — sie bleibt beim Umwandeln.
   *
   * Pflicht und nicht optional: Der Dialog nennt sie im Text, und ein
   * Aufrufer, der sie vergisst, muss am Typprüfer scheitern. Mit `?` blieb
   * genau das unbemerkt — die Gegenprobe „Nummer nicht an den Dialog
   * gegeben" lief grün durch alle 28 Tests (29.10.2026).
   */
  leadNummer: string;
  onConverted: (customerId: string) => void;
}

export function ConvertLeadModal({ isOpen, onClose, leadId, leadName, leadNummer, onConverted }: ConvertLeadModalProps) {
  const queryClient = useQueryClient();

  const { control, handleSubmit } = useForm<ConvertFormData>({
    defaultValues: {
      customer_business_type: '',
      industry: '',
      street: '',
      postal_code: '',
      city: '',
      website: '',
      vat_number: '',
      managed_objects_count: '',
      contact_email: '',
      contact_role: '',
    },
  });

  const mutation = useMutation({
    mutationFn: (data: ConvertFormData) =>
      leadsApi.convert(leadId, {
        customer_business_type: data.customer_business_type || undefined,
        industry: data.industry || undefined,
        street: data.street || undefined,
        postal_code: data.postal_code || undefined,
        city: data.city || undefined,
        website: data.website || undefined,
        vat_number: data.vat_number || undefined,
        managed_objects_count: data.managed_objects_count ? parseInt(data.managed_objects_count) : undefined,
        contact_email: data.contact_email || undefined,
        contact_role: data.contact_role || undefined,
      }),
    onSuccess: (customer) => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      toast.success(`„${leadName}“ ist jetzt Kunde`);
      onClose();
      onConverted(customer.id);
    },
    // Hier stand bis zum 29.10.2026 `formatApiError(err) || 'Lead liess sich
    // nicht umwandeln'`. Der deutsche Text hinter `||` kam nie zum Zug:
    // `formatApiError` faellt auf `err.message` zurueck, also auf das englische
    // „Network Error" von axios, und das ist nicht leer. Seit dem 29.10. ist
    // dieser Dialog zusaetzlich aus der Leadliste erreichbar („Als Kunde
    // anlegen" im Statusmenue) — ein englischer Satz an dieser Stelle waere
    // damit haeufiger geworden, nicht seltener.
    onError: (err) => {
      toast.error(apiFehlerText(err, 'Das Umwandeln'));
    },
  });

  return (
    <Dialog open={isOpen} onOpenChange={(offen) => !offen && onClose()}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>{`„${leadName}“ als Kunde anlegen`}</DialogTitle>
          {/* Bis zum 29.10.2026 stand hier „Die Kundennummer (K-00000) wird
              automatisch vergeben" — und das stimmte, denn `convert` zog bei
              jedem Aufruf eine neue, obwohl der Lead seine K-Nummer schon
              trug und sie oben auf seiner Seite steht. Seit dem Rückweg darf
              sie das nicht mehr: Projektnummern frieren sie beim Anlegen ein.
              Der Satz sagt jetzt, was passiert. */}
          <DialogDescription>
            {`Kundendaten ergänzen. Die Kundennummer ${leadNummer} bleibt.`}
          </DialogDescription>
        </DialogHeader>
      <form onSubmit={handleSubmit((data) => mutation.mutate(data))} className="space-y-4">

        {/* Business Classification */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Controller
            name="customer_business_type"
            control={control}
            render={({ field }) => (
              <Field label="Geschäftstyp">
                {(f) => <NativeSelect {...f} {...field} options={[{ value: '', label: 'Keine Angabe' }, ...businessTypeOptions]} />}
              </Field>
            )}
          />
          <Controller
            name="industry"
            control={control}
            render={({ field }) => (
              <Field label="Branche">
                {(f) => <NativeSelect {...f} {...field} options={[{ value: '', label: 'Keine Angabe' }, ...industryOptions]} />}
              </Field>
            )}
          />
        </div>

        {/* Address */}
        <div className="border-t border-border pt-4">
          <h3 className="mb-3 t-body-strong">Adresse</h3>
          <Controller
            name="street"
            control={control}
            render={({ field }) => <TextField {...field} label="Straße" />}
          />
          <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Controller
              name="postal_code"
              control={control}
              render={({ field }) => <TextField {...field} label="PLZ" />}
            />
            <div className="sm:col-span-2">
              <Controller
                name="city"
                control={control}
                render={({ field }) => <TextField {...field} label="Ort" />}
              />
            </div>
          </div>
        </div>

        {/* Additional Info */}
        <div className="border-t border-border pt-4">
          <h3 className="mb-3 t-body-strong">Weitere Angaben</h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Controller
              name="website"
              control={control}
              render={({ field }) => <TextField {...field} label="Website" placeholder="www.beispiel.de" />}
            />
            <Controller
              name="vat_number"
              control={control}
              render={({ field }) => <TextField {...field} label="USt-IdNr." placeholder="DE123456789" />}
            />
          </div>
          <div className="mt-3">
            <Controller
              name="managed_objects_count"
              control={control}
              render={({ field }) => <TextField {...field} label="Verwaltete Objekte" type="number" />}
            />
          </div>
        </div>

        {/* Contact Update */}
        <div className="border-t border-border pt-4">
          <h3 className="mb-3 t-body-strong">Ansprechpartner ergänzen</h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Controller
              name="contact_email"
              control={control}
              render={({ field }) => <TextField {...field} label="E-Mail" type="email" />}
            />
            <Controller
              name="contact_role"
              control={control}
              render={({ field }) => <TextField {...field} label="Position" />}
            />
          </div>
        </div>

        <DialogFooter className="border-t border-border pt-4">
          <Button type="button" variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button type="submit" variant="primary" loading={mutation.isPending}>
            Als Kunde anlegen
          </Button>
        </DialogFooter>
      </form>
      </DialogContent>
    </Dialog>
  );
}
